import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { blogService, categoriesService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
    HiOutlineDocumentText,
    HiOutlinePlus,
    HiOutlinePencil,
    HiOutlineTrash,
    HiOutlineEye,
    HiOutlineTag,
    HiOutlineCalendar
} from 'react-icons/hi';
import './EventsManage.css';

const MEDIA_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

const BlogManage = () => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    useAuth();

    useEffect(() => {
        loadPosts();
        loadCategories();
    }, []);

    const loadPosts = async () => {
        try {
            const response = await blogService.getAll();
            setPosts(response.data.posts || []);
        } catch (error) {
            toast.error('Error al cargar artículos');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await categoriesService.getAll();
            setCategories(response.data);
        } catch (error) {
            console.error('Error al cargar categorías:', error);
        }
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`¿Estás seguro de eliminar el artículo "${title}"?`)) {
            return;
        }

        try {
            await blogService.delete(id);
            toast.success('Artículo eliminado exitosamente');
            loadPosts();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al eliminar artículo');
            console.error(error);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            draft: <span className="badge badge-warning">Borrador</span>,
            published: <span className="badge badge-success">Publicado</span>,
            scheduled: <span className="badge badge-info">Programado</span>,
            archived: <span className="badge badge-gray">Archivado</span>
        };
        return badges[status] || <span className="badge badge-gray">{status}</span>;
    };

    if (loading && posts.length === 0) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando artículos...</p>
            </div>
        );
    }

    return (
        <div className="content-manage">
            <div className="content-header">
                <div className="header-info">
                    <h1>Gestión de Blog</h1>
                    <p>Crea y administra artículos del blog</p>
                </div>
                <button 
                    className="btn btn-primary" 
                    onClick={() => navigate('/blog/new')}
                >
                    <HiOutlinePlus />
                    <span>Nuevo Artículo</span>
                </button>
            </div>

            {posts.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <HiOutlineDocumentText />
                    </div>
                    <h3>No hay artículos</h3>
                    <p>Crea tu primer artículo para empezar</p>
                    <button 
                        className="btn btn-primary" 
                        onClick={() => navigate('/blog/new')}
                    >
                        <HiOutlinePlus />
                        <span>Crear Artículo</span>
                    </button>
                </div>
            ) : (
                <div className="events-grid">
                    {posts.map(post => (
                        <div key={post.id} className="event-card card">
                            {post.featured_image && (
                                <div className="event-image-container">
                                    <img 
                                        src={`${MEDIA_URL}${post.featured_image}`} 
                                        alt={post.title_es}
                                        className="event-image"
                                    />
                                </div>
                            )}
                            <div className="card-body">
                                <div className="event-header-row">
                                    <h3 className="event-title">{post.title_es}</h3>
                                    {getStatusBadge(post.status)}
                                </div>
                                
                                <div className="event-details">
                                    {post.category_name && (
                                        <div className="detail-item">
                                            <HiOutlineTag />
                                            <span>{post.category_name}</span>
                                        </div>
                                    )}
                                    {post.published_at && (
                                        <div className="detail-item">
                                            <HiOutlineCalendar />
                                            <span>{new Date(post.published_at).toLocaleDateString('es-ES')}</span>
                                        </div>
                                    )}
                                    <div className="detail-item">
                                        <HiOutlineEye />
                                        <span>{post.views || 0} vistas</span>
                                    </div>
                                </div>

                                {post.excerpt_es && (
                                    <p className="event-description">{post.excerpt_es}</p>
                                )}

                                <div className="event-actions">
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => navigate(`/blog/edit/${post.id}`)}
                                    >
                                        <HiOutlinePencil />
                                        <span>Editar</span>
                                    </button>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDelete(post.id, post.title_es)}
                                    >
                                        <HiOutlineTrash />
                                        <span>Eliminar</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BlogManage;
